import React, { FunctionComponent } from "react";
import DetailItem from "./DetailItem";
import { useTranslate } from "../helpers";
import {
  Plan,
  SupplementalBenefitCategoryTypeNames,
  PlanCategoryType,
  PlanSNPType,
} from "../@types";
import LimitsApplyAccordion from "./LimitsApplyAccordion";
import { FormattedMessage } from "react-intl-phraseapp";

const getPlanCategoryBenefitLabel = (
  category: string,
  planType: PlanCategoryType
): string => {
  const PlanCategoryMessages = {
    [PlanCategoryType.MSA]: "plan_details.other_benefits.msa_copay_message",
    [PlanCategoryType.MEDICARE_MEDICAID_PLAN]:
      "plan_details.other_benefits.mmp_copay_message",
  };
  const labels = {
    [SupplementalBenefitCategoryTypeNames.SB_CAT_OPIOID_TREATMENT_SERVICES]: {
      ...PlanCategoryMessages,
    },
    [SupplementalBenefitCategoryTypeNames.SB_CAT_DIALYSIS_SERVICES]: {
      ...PlanCategoryMessages,
    },
  };

  return (labels[category] && labels[category][planType]) || "";
};

interface OtherServicesBenefitItemProps {
  plan: Plan;
  label: string;
  category: SupplementalBenefitCategoryTypeNames;
}

const OtherServicesBenefitItem: FunctionComponent<OtherServicesBenefitItemProps> = ({
  plan,
  label,
  category,
}) => {
  const t = useTranslate();

  let inNetworkCopay = "";
  let inNetworkCoinsurance = "";
  let outOfNetworkCopay = "";
  let outOfNetworkCoinsurance = "";
  let authorizationRequired = false;
  let referralRequired = false;
  let useInNetworkLabel = true;
  let useCustomString = false;

  const servicesCategory = plan.additional_supplemental_benefits?.other_benefits.find(
    c => c.category === category
  );

  if (servicesCategory?.benefits.length) {
    const benefit = servicesCategory.benefits[0];

    if (benefit.info) {
      const {
        authorization_required,
        referral_required,
        coins_max_pct,
        coins_min_pct,
        copay_max,
        copay_min,
        oon_coins_max_pct,
        oon_coins_min_pct,
        oon_copay_max,
        oon_copay_min,
      } = benefit.info;

      authorizationRequired = authorization_required;
      referralRequired = referral_required;
      const { non_zero_dollar_dsnp } = plan;

      if (copay_min !== null) {
        inNetworkCopay = `$${copay_min}`;

        if (copay_max !== null && copay_max !== copay_min) {
          inNetworkCopay = `$${copay_min}-$${copay_max}`;
        }

        // If non $0 DSNP, $0 or $5
        if (non_zero_dollar_dsnp && copay_min > 0) {
          inNetworkCopay = `$0 ${t("or")} $${copay_min}`;
        }
      }

      if (coins_min_pct !== null) {
        inNetworkCoinsurance = `${coins_min_pct}%`;

        if (coins_max_pct !== null && coins_max_pct !== coins_min_pct) {
          inNetworkCoinsurance = `${coins_min_pct}%-${coins_max_pct}%`;
        }

        // If non $0 DSNP, 0% or 5%
        if (non_zero_dollar_dsnp && coins_min_pct > 0) {
          inNetworkCoinsurance = `0% ${t("or")} ${coins_min_pct}%`;
        }
      }

      if (oon_copay_min !== null) {
        outOfNetworkCopay = `$${oon_copay_min}`;

        if (oon_copay_max !== null && oon_copay_max !== oon_copay_min) {
          outOfNetworkCopay = `$${oon_copay_min}-$${oon_copay_max}`;
        }
      }

      if (oon_coins_min_pct !== null) {
        outOfNetworkCoinsurance = `${oon_coins_min_pct}%`;

        if (
          oon_coins_max_pct !== null &&
          oon_coins_max_pct !== oon_coins_min_pct
        ) {
          outOfNetworkCoinsurance = `${oon_coins_min_pct}%-${oon_coins_max_pct}%`;
        }
      }
    }
  }

  if (
    [PlanCategoryType.MEDICARE_MEDICAID_PLAN, PlanCategoryType.MSA].includes(
      plan.category
    ) ||
    (plan.category === PlanCategoryType.HMO &&
      plan.snp_type === PlanSNPType.SNP_TYPE_DUAL_ELIGIBLE)
  ) {
    if (inNetworkCoinsurance === "") {
      inNetworkCoinsurance = getPlanCategoryBenefitLabel(
        category,
        plan.category
      );
      useCustomString = true;
    }
    useInNetworkLabel = false;
  }

  return (
    <DetailItem
      label={label}
      limitsApply={
        <LimitsApplyAccordion
          descriptors={{
            authorization_required: authorizationRequired,
            referral_required: referralRequired,
            plan_limits: false,
          }}
        />
      }
      detail={
        <ul id="other-services-benefit-item-list" className="ds-c-list--bare">
          {(inNetworkCopay || inNetworkCoinsurance) && (
            <li>
              <div className="ds-u-display--flex">
                {useInNetworkLabel && (
                  <p>{t("plan_details.benefit.in_network")}</p>
                )}
                <ul
                  className={`ds-c-list--bare ${
                    useInNetworkLabel
                      ? "ds-u-margin-left--1"
                      : "ds-u-margin-left--0"
                  }`}
                >
                  {!useCustomString && inNetworkCopay && (
                    <li id="osbl-in-network-copay">
                      <FormattedMessage
                        id="plan_details.other_services.copay"
                        values={{ copayRange: inNetworkCopay }}
                      />
                    </li>
                  )}
                  {inNetworkCoinsurance && (
                    <li id="osbl-in-network-coinsurance">
                      {useCustomString ? (
                        t(inNetworkCoinsurance)
                      ) : (
                        <FormattedMessage
                          id="plan_details.other_services.coinsurance"
                          values={{ coinsuranceRange: inNetworkCoinsurance }}
                        />
                      )}
                    </li>
                  )}
                </ul>
              </div>
            </li>
          )}

          {(outOfNetworkCopay || outOfNetworkCoinsurance) && (
            <li>
              <div className="ds-u-display--flex">
                <p>{t("plan_details.benefit.oo_network")}</p>
                <ul className="ds-c-list--bare  ds-u-margin-left--1">
                  {outOfNetworkCopay && (
                    <li id="osbl-oo-network-copay">
                      <FormattedMessage
                        id="plan_details.other_services.copay"
                        values={{ copayRange: outOfNetworkCopay }}
                      />
                    </li>
                  )}
                  {outOfNetworkCoinsurance && (
                    <li id="osbl-oo-network-coinsurance">
                      <FormattedMessage
                        id="plan_details.other_services.coinsurance"
                        values={{
                          coinsuranceRange: outOfNetworkCoinsurance,
                        }}
                      />
                    </li>
                  )}
                </ul>
              </div>
            </li>
          )}
        </ul>
      }
    />
  );
};

export default OtherServicesBenefitItem;
